import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  KeyRound,
  UserCheck,
  CreditCard,
  Lock,
  Smartphone,
  RotateCcw,
  ShieldCheck,
  AlertTriangle,
  Clock,
  SignalHigh,
  Globe2,
  Route as RouteIcon,
  Filter,
  BadgeCheck,
  Eye,
  Gauge,
  Layers,
  Network,
  Headphones,
  Activity,
  CheckCircle,
  Copy,
  RefreshCw,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import FlatIcon from "@/components/site/FlatIcon";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import CTASection from "@/components/site/CTASection";
import Seo from "@/components/Seo";

const workflowSteps = [
  {
    title: "1. The user starts an action",
    description: "A login, payment, account change or verification step begins inside your application.",
  },
  {
    title: "2. Your application generates the OTP",
    description: "Your systems create the one-time password and decide its format, validity window and retry rules.",
  },
  {
    title: "3. The code is sent by SMS",
    description: "The message travels from your platform through the messaging provider's routes to the user's mobile network.",
  },
  {
    title: "4. The user enters the code",
    description: "The user reads the message and types the code back into your login, payment or verification screen.",
  },
  {
    title: "5. Your application validates it",
    description: "Your systems compare the entered code with the one issued and check it is still within its validity window.",
  },
  {
    title: "6. The action completes",
    description: "If validation succeeds, the login completes, the payment is confirmed, or the account change proceeds.",
  },
];

const useCases = [
  {
    title: "Login Verification",
    description:
      "Confirm that the person signing in controls the registered phone number, as a second factor or as a passwordless step.",
    Icon: KeyRound,
    color: "text-indigo",
  },
  {
    title: "Account Registration",
    description:
      "Verify the number a new user provides during signup before the account becomes active or reachable.",
    Icon: UserCheck,
    color: "text-sky",
  },
  {
    title: "Transaction Confirmation",
    description:
      "Ask for a code before a payment, transfer or payout completes, so the account holder explicitly approves it.",
    Icon: CreditCard,
    color: "text-lime",
  },
  {
    title: "Password Reset",
    description:
      "Deliver reset codes during recovery flows so access returns to the person who controls the registered number.",
    Icon: RotateCcw,
    color: "text-violet",
  },
  {
    title: "Account Change Verification",
    description:
      "Confirm changes to credentials, contact details, limits or payout information with a code sent to the number on file.",
    Icon: Lock,
    color: "text-amber",
  },
  {
    title: "Step-Up Authentication",
    description:
      "Add a code check when a session or action crosses a risk threshold, instead of blocking the user outright.",
    Icon: ShieldCheck,
    color: "text-pink",
  },
  {
    title: "Device and Number Binding",
    description:
      "Tie a new device or a newly added number to an existing account by proving control of the number at setup time.",
    Icon: Smartphone,
    color: "text-navy",
  },
  {
    title: "High-Risk Action Approval",
    description:
      "Require an explicit confirmation for first-time payees, large amounts or actions your risk rules flag for review.",
    Icon: AlertTriangle,
    color: "text-rose",
  },
];

const deliveryProblems = [
  {
    title: "Carrier Filtering",
    description:
      "Operator and regulatory filtering can hold or block traffic based on content, sender and sending patterns, sometimes without clear feedback to the sender.",
    Icon: Filter,
    color: "text-violet",
  },
  {
    title: "Sender ID Issues",
    description:
      "Sender identifier rules differ between countries. Where registration is required or IDs are overwritten, the message can be delayed, blocked or look unfamiliar to the user.",
    Icon: BadgeCheck,
    color: "text-indigo",
  },
  {
    title: "Route Quality",
    description:
      "Traffic passed through long chains of intermediaries is less predictable and harder to investigate, which is a poor fit for codes that expire within seconds.",
    Icon: RouteIcon,
    color: "text-lime",
  },
  {
    title: "Latency and Expiration",
    description:
      "Short validity windows are good security practice, but they make latency expensive: a slow route can turn a valid code into an expired one.",
    Icon: Clock,
    color: "text-amber",
  },
  {
    title: "Duplicate Messages",
    description:
      "Users who wait request another code, and retries through the same or different paths can deliver out of order, leaving the user unsure which code is current.",
    Icon: Copy,
    color: "text-sky",
  },
  {
    title: "Inconsistent International Delivery",
    description:
      "A route that performs well in one market can behave differently in another, so OTP quality often varies by destination within the same platform.",
    Icon: Globe2,
    color: "text-rose",
  },
  {
    title: "Lack of Delivery Visibility",
    description:
      "Without meaningful reporting, teams cannot tell whether the problem sits in their application, the messaging provider or the destination network.",
    Icon: Eye,
    color: "text-navy",
  },
  {
    title: "Retry and Fallback Gaps",
    description:
      "Retry logic that resends over a degraded path repeats the failure. Sensible fallback needs route alternatives and the data to know when to use them.",
    Icon: RefreshCw,
    color: "text-pink",
  },
];

const improvementPoints = [
  {
    title: "Use Transactional-Grade Routes",
    description:
      "Send OTP traffic over routes intended for authentication messaging rather than the cheapest available option. Route choice is the single biggest delivery variable.",
    Icon: RouteIcon,
    color: "text-indigo",
  },
  {
    title: "Register Sender IDs",
    description:
      "Where local rules require sender ID registration, complete it. A recognisable, compliant sender improves both reach and user trust.",
    Icon: BadgeCheck,
    color: "text-amber",
  },
  {
    title: "Keep Messages Clean",
    description:
      "Short, consistent message structure with the code easy to find attracts less filtering scrutiny than promotional-looking content.",
    Icon: SignalHigh,
    color: "text-sky",
  },
  {
    title: "Separate Traffic Types",
    description:
      "Keep OTP and authentication traffic apart from marketing sending, so promotional patterns do not affect the routes your codes depend on.",
    Icon: Layers,
    color: "text-lime",
  },
  {
    title: "Monitor Per Destination",
    description:
      "Track delivery and latency per country and per operator rather than as one global figure, so a degrading market is visible before users report it.",
    Icon: Activity,
    color: "text-violet",
  },
  {
    title: "Keep Alternatives Ready",
    description:
      "Alternative routing paths matter when a network or connection degrades, so retry logic has somewhere better to go than the same failing path.",
    Icon: RefreshCw,
    color: "text-rose",
  },
];

const howWeSupport = [
  {
    title: "Telecom-Native Expertise",
    description:
      "Yootelco comes from the telecommunications side of messaging, with more than 17 years of experience in voice and messaging. That background shapes how OTP traffic is routed and investigated.",
    Icon: Network,
    color: "text-indigo",
  },
  {
    title: "Quality Routing",
    description:
      "Authentication traffic is routed through connections chosen for reliability rather than lowest cost, with routing decisions made per destination and traffic type.",
    Icon: RouteIcon,
    color: "text-amber",
  },
  {
    title: "Global SMS Connectivity",
    description:
      "Codes are delivered through operator and carrier connectivity across the markets our clients serve, supporting consistent reach as user bases expand.",
    Icon: Globe2,
    color: "text-sky",
  },
  {
    title: "Delivery Monitoring",
    description:
      "Traffic is monitored so performance changes are noticed and acted on, and so your team has delivery insight to work from when investigating an issue.",
    Icon: Activity,
    color: "text-lime",
  },
  {
    title: "Responsive Support",
    description:
      "A team built for enterprise clients works directly with your engineering, product and operations people instead of routing you through a ticket queue.",
    Icon: Headphones,
    color: "text-violet",
  },
  {
    title: "Flexible Connectivity",
    description:
      "When a route looks wrong we investigate it at operator level and change it, rather than leaving traffic in place and reporting the same numbers back.",
    Icon: Gauge,
    color: "text-rose",
  },
];

const faqs = [
  {
    question: "What is OTP SMS verification?",
    answer:
      "OTP SMS verification is the use of a one-time password sent by text message to confirm that a user controls the phone number registered to an account or action. The business's application generates a short-lived code, delivers it to the user's handset via SMS, and validates the code the user types back. It is widely used for login verification, transaction confirmation, password reset and account changes because SMS reaches almost any mobile phone without an app or data connection. It is an authentication mechanism, not a complete identity verification process on its own.",
  },
  {
    question: "How does SMS OTP authentication work?",
    answer:
      "The user begins a login, payment or verification flow. The business's application generates a unique, time-limited code and sends it to the registered phone number through a messaging provider. The user reads the SMS and enters the code into the application, which checks that it matches and is still valid, then completes the action if it is. The messaging provider supplies the delivery layer between the platform and the mobile networks, while code generation, expiry and validation stay inside the business's own systems.",
  },
  {
    question: "Why are OTP SMS messages delayed?",
    answer:
      "Common causes include indirect routing to the destination network, carrier or regulatory filtering, sender ID rules that are not met in a particular country, congestion on shared routes, and handsets that are temporarily unreachable, ported or switched off. Each intermediary in the delivery path adds processing time. Because OTP codes are usually valid for a short window, even a modest delay can leave users requesting a second code, which adds cost and confusion. Route quality and per-destination monitoring are the usual levers for improvement.",
  },
  {
    question: "How can businesses improve OTP SMS delivery?",
    answer:
      "Send codes over routes built for transactional authentication traffic rather than the lowest-cost option, and work with a provider that has direct operator connectivity in your main markets. Register sender IDs where local rules require it, keep messages short and consistent, and separate OTP traffic from promotional sending. Monitor delivery and latency per destination and per operator, keep alternative routing available so retries do not repeat the same failure, and investigate market-specific problems at route level instead of resending blindly.",
  },
  {
    question: "Is SMS OTP the same as identity verification?",
    answer:
      "No. An SMS OTP confirms that the person completing an action can receive messages at a registered phone number. Identity verification is a broader process that can combine document checks, database or registry lookups, biometrics, device signals and behavioural analysis to establish who a user actually is. An OTP is often one step inside that wider process, such as the phone verification stage during onboarding, but receiving a code should not be described as complete identity verification by itself.",
  },
  {
    question: "Can OTP SMS be delivered internationally?",
    answer:
      "Yes. SMS OTP can be delivered to users in different countries through international messaging routes, and it is one of the most common ways platforms verify users across borders. International delivery does introduce variables: each destination has its own operators, filtering practices, sender ID requirements and latency characteristics, so performance can differ between markets. Reliable international OTP delivery depends on quality routing, direct carrier connectivity where possible, and monitoring that shows how each destination is actually performing.",
  },
];

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "OTP SMS Verification",
  serviceType: "A2P SMS messaging",
  description:
    "Transactional OTP SMS for authentication workflows, including login verification, transaction confirmation, password reset, account change verification and step-up authentication.",
  provider: {
    "@type": "Organization",
    name: "Yootelco",
    url: "https://yootelco.com",
  },
  areaServed: "Worldwide",
  audience: {
    "@type": "Audience",
    audienceType:
      "Fintech companies, banks, payment providers, ecommerce businesses, digital platforms and enterprises",
  },
  url: "https://yootelco.com/use-cases/otp-verification",
};

const UseCaseOtpVerification = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <main className="industries-page min-h-screen bg-white">
      <Seo
        title="OTP SMS Verification | Reliable Authentication Messaging | Yootelco"
        description="Reliable OTP SMS for authentication, login verification and account security. Improve verification message delivery with quality routing and global SMS connectivity."
        path="/use-cases/otp-verification"
        faqs={faqs}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>
      <SiteHeader />

      {/* Hero */}
      <section className="pb-16 pt-[120px] md:pb-20 md:pt-[140px]" style={{ backgroundImage: "var(--gradient-hero)" }}>
        <div className="container">
          <div className="mx-auto max-w-[800px] text-center">
            <span className="inline-block rounded-full bg-primary/15 px-4 py-1.5 text-[13px] font-bold uppercase tracking-wider text-foreground">
              Authentication Messaging Use Case
            </span>
            <h1 className="mt-5 font-display text-[44px] font-bold leading-[1.05] tracking-tight text-foreground md:text-[60px] [text-wrap:balance]">
              OTP SMS Verification for Secure Authentication
            </h1>
            <p className="mx-auto mt-[18px] max-w-[700px] text-[15px] font-medium leading-[1.55] text-muted-foreground md:text-[16px] [text-wrap:balance]">
              Businesses use one-time passwords delivered by SMS to verify logins, confirm transactions, protect account recovery and secure sensitive changes. Yootelco delivers that authentication traffic with a telecom-native approach: quality routing, timely delivery, global SMS connectivity and delivery monitoring when a destination needs attention.
            </p>
            <Link
              to="/contact"
              className="mt-[28px] inline-flex rounded-[6px] bg-primary px-[31px] py-[13px] text-[15px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
            >
              Contact Sales
            </Link>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-5">
              {[
                { label: "OTP SMS", Icon: KeyRound },
                { label: "Login Verification", Icon: Lock },
                { label: "Transactional A2P SMS", Icon: SignalHigh },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2 text-[13px] font-semibold text-muted-foreground">
                  <item.Icon className="h-4 w-4 text-primary" strokeWidth={2.2} />
                  {item.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">What Is OTP SMS Verification?</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            OTP SMS verification uses a one-time password sent by text message to confirm that the person completing an action controls the phone number attached to it. The code is generated by your application, delivered to the user's handset, entered back into your flow and validated. It appears at login, during payments, in password reset and before sensitive account changes.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            It is worth being precise about what it proves. An SMS OTP is an authentication and verification mechanism: it confirms control of a number at a moment in time. It does not by itself establish a user's complete identity, and no single factor guarantees complete account security. Stronger setups combine OTP with device signals, behavioural analysis, risk scoring and, where needed, broader identity verification checks.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            What SMS offers is reach and immediacy: no app install, no push permissions, no data connection required. For fintech companies, banks, payment providers, ecommerce businesses and digital platforms, that is why OTP traffic is treated as{" "}
            <Link to="/solutions/a2p-sms" className="font-semibold text-primary underline-offset-4 hover:underline">
              A2P SMS
            </Link>{" "}
            infrastructure rather than a marketing channel.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">How SMS OTP Authentication Works</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            The workflow is deliberately simple, and the same pattern appears in login, payment confirmation and recovery flows across most platforms.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {workflowSteps.map((item) => (
            <article
              key={item.title}
              className="flex flex-col rounded-[18px] border border-border/60 bg-card px-6 py-8 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
            >
              <h3 className="text-[17px] font-bold text-foreground">{item.title}</h3>
              <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
            </article>
          ))}
        </div>
        <div className="mx-auto mt-[28px] max-w-[860px] text-center">
          <p className="text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            The responsibilities are split clearly. Your application generates the code, sets its validity window, enforces retry rules and validates what the user enters. Yootelco provides the messaging layer in between: getting the code from your platform to the user's phone, quickly and reliably, wherever that user happens to be.
          </p>
        </div>
      </section>

      {/* Why Speed Matters */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">Why OTP Delivery Speed Matters</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Every OTP is time-sensitive by design. Codes are issued with short validity windows because that limits their usefulness to anyone who intercepts them. The side effect is that delivery latency directly consumes the window: a code that takes forty seconds to arrive may leave the user twenty seconds to use it, and a code that takes two minutes may arrive already expired.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Slow codes also change user behaviour. A user staring at a waiting screen requests another code, and retries multiply traffic, cost and the chance of two messages arriving out of order. Consistent arrival within seconds keeps the flow single-attempt, which is cheaper, simpler and easier for users to understand.
          </p>
        </div>
      </section>

      {/* Delivery Problems */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">Common OTP SMS Delivery Problems</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Most OTP problems are not caused by the code or the message. They come from how traffic is routed, how each destination network treats it, and how much visibility the sending team has when conditions change. These are the issues product and engineering teams report most often.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {deliveryProblems.map((item) => (
            <article
              key={item.title}
              className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
            >
              <FlatIcon Icon={item.Icon} color={item.color} />
              <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
              <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
            </article>
          ))}
        </div>
        <div className="mx-auto mt-[28px] max-w-[860px] text-center">
          <p className="text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            No provider removes all of these issues, since operator behaviour and local regulation sit outside any single supplier's control. What route quality, monitoring and troubleshooting can improve is how often codes arrive on time, and how quickly a problem in one market is identified and corrected.
          </p>
        </div>
      </section>

      {/* Improve Delivery */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">How Businesses Can Improve OTP Delivery</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Better OTP delivery is mostly a set of operational decisions rather than a single feature. The practices below are the ones that consistently make the difference.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {improvementPoints.map((item) => (
            <article
              key={item.title}
              className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
            >
              <FlatIcon Icon={item.Icon} color={item.color} />
              <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
              <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Use Cases */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">OTP SMS Use Cases</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            The specific flows differ between a payment platform, a digital bank, a marketplace and an enterprise application, but the verification points overlap heavily. These are the ones teams bring to us most often.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {useCases.map((item) => (
            <article
              key={item.title}
              className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
            >
              <FlatIcon Icon={item.Icon} color={item.color} />
              <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
              <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
            </article>
          ))}
        </div>
        <div className="mx-auto mt-[28px] max-w-[860px] text-center">
          <p className="text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            In{" "}
            <Link to="/industries/fintechs" className="font-semibold text-primary underline-offset-4 hover:underline">
              fintech
            </Link>{" "}
            and{" "}
            <Link to="/industries/banks-financial-institutions" className="font-semibold text-primary underline-offset-4 hover:underline">
              banking and financial services
            </Link>
            , OTP typically sits alongside{" "}
            <Link to="/use-cases/transactional-alerts" className="font-semibold text-primary underline-offset-4 hover:underline">
              transactional alerts
            </Link>{" "}
            and{" "}
            <Link to="/use-cases/fraud-prevention" className="font-semibold text-primary underline-offset-4 hover:underline">
              fraud and security messaging
            </Link>
            . In enterprise applications it most often covers workforce login, customer portal access and approval workflows.
          </p>
        </div>
      </section>

      {/* Global */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">Global OTP SMS Delivery</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            User bases rarely sit in one country. Customers register from different markets, travel, and hold numbers from more than one operator. Each destination brings its own mobile networks, filtering practices, sender ID requirements and latency characteristics, so OTP delivery that performs well domestically does not automatically perform well elsewhere.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Route differences show up as varying latency and inconsistent delivery behaviour between regions. Direct carrier connectivity matters because it shortens the path between your platform and the user's handset. Fewer intermediaries means fewer places for a code to be delayed or filtered, and clearer answers when one destination starts behaving differently from the rest.
          </p>
          <div className="mt-[24px] mx-auto max-w-[640px] text-left">
            {[
              "Direct connections to mobile network operators",
              "Routing configured per destination and traffic type",
              "Delivery monitoring across international routes",
              "Support for sender ID requirements where local rules apply",
              "Route changes made when a destination underperforms",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3 py-3">
                <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-primary/10 text-primary">
                  <CheckCircle className="h-3.5 w-3.5" strokeWidth={2.5} />
                </span>
                <p className="text-[15px] leading-[1.55] text-muted-foreground">{item}</p>
              </div>
            ))}
          </div>
          <p className="mt-[24px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            You can read more about how we approach{" "}
            <Link to="/coverage-routes" className="font-semibold text-primary underline-offset-4 hover:underline">
              coverage and routing
            </Link>{" "}
            across international destinations.
          </p>
        </div>
      </section>

      {/* OTP vs Identity Verification / 2FA */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">OTP SMS, Identity Verification and 2FA</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            These terms are often used interchangeably, and the distinctions matter. An SMS OTP is a method: a one-time code that confirms control of a phone number at a specific moment. Two-factor authentication is a design pattern: combining something the user knows or has with a second factor, which an SMS OTP can serve as. Identity verification is a broader process that may include document checks, database lookups, biometrics and other signals to establish who a user actually is.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            An OTP can be one step inside either pattern: the second factor in a 2FA login, or the phone verification stage of a wider{" "}
            <Link to="/use-cases/identity-verification" className="font-semibold text-primary underline-offset-4 hover:underline">
              identity verification
            </Link>{" "}
            process. But receiving a code should not be described as complete identity verification by itself, and OTP alone should not be treated as the whole of an account security strategy.
          </p>
        </div>
      </section>

      {/* How Yootelco Supports */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">How Yootelco Supports OTP Messaging</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Yootelco does not generate or validate your codes, and we do not claim to verify identities. What we provide is the messaging layer your authentication workflows depend on, built around route quality, monitoring and hands-on support rather than volume for its own sake.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {howWeSupport.map((item) => (
            <article
              key={item.title}
              className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
            >
              <FlatIcon Icon={item.Icon} color={item.color} />
              <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
              <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
            </article>
          ))}
        </div>
      </section>

      {/* CTA */}
      <CTASection
        title="Need Reliable Delivery for OTP and Authentication Messaging?"
        description="Tell us about your OTP, login verification and authentication messaging requirements and we'll show you how Yootelco can support them."
        useCase="OTP SMS Verification"
      />

      {/* FAQ */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Frequently Asked Questions</h2>
        </div>
        <div className="mx-auto mt-10 max-w-[1120px]">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((item, index) => (
              <AccordionItem key={item.question} value={`item-${index}`} className="border-b border-border">
                <AccordionTrigger className="py-6 text-left text-lg font-bold hover:no-underline md:text-xl">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="pb-6 pr-12 text-base leading-7 text-muted-foreground">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Conclusion */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">Authentication Messaging That Arrives on Time</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            OTP is judged at the moment of use. A code that arrives within seconds lets a user finish signing in, confirming a payment or recovering an account without thinking about the infrastructure behind it. A code that arrives late, or not at all, turns that moment into retries, support contacts and friction your team then has to explain.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Timely OTP delivery comes down to delivery quality: latency, route choice, sender presentation, redundancy and the visibility to see when a destination changes behaviour. Yootelco's telecom-native approach, built on more than 17 years of voice and messaging experience, direct operator connectivity, monitoring and responsive support, is designed for exactly this kind of enterprise authentication traffic. Talk to our team about your requirements, or use the{" "}
            <Link to="/contact" className="font-semibold text-primary underline-offset-4 hover:underline">
              contact form
            </Link>{" "}
            to share the destinations that matter most.
          </p>
          <Link
            to="/contact"
            className="mt-[28px] inline-flex rounded-[6px] bg-primary px-[31px] py-[13px] text-[15px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
          >
            Contact Sales
          </Link>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
};

export default UseCaseOtpVerification;
